import { UserRole } from '@app/core/auth/auth.repository';

//
// ------------------------------ DATA STRUCTURE
//
export interface CommentsHeaders {
  title: string;
  avatar: string;
}

export interface CommentsOptions {
  view: 'default';
}

export interface Comment {
  id: number;
  sender_id: number;
  sender_role: UserRole;
  sender_name: string;
  message: string;
  timestamp: string;
  avatar: string;
  isDelivered: boolean;
  isRead: boolean;
}

export interface CommentsVersion {
  version_tag: number;
  timestamp: string;
  comments: Comment[];
  last_comment: Comment | null;
}

/** Canonical displayable-body rule — mirrored by API unread-counts and ghost reconciliation. */
export function commentHasDisplayableBody(
  message: string | null | undefined,
  attachedFiles?: unknown[] | null,
): boolean {
  if (attachedFiles?.length) {
    return true;
  }
  return (message ?? '').replace(/<[^>]*>/g, '').trim().length > 0;
}

/** Drop ghost comments (metadata only, no body) from a CommentsVersion payload. */
export function sanitizeCommentsVersion(version: CommentsVersion): CommentsVersion {
  const comments = (version.comments ?? []).filter((comment) =>
    commentHasDisplayableBody(comment.message, (comment as Comment & { attached_files?: unknown[] }).attached_files),
  );
  const lastComment = comments.length > 0 ? comments[comments.length - 1] : null;

  return {
    ...version,
    version_tag: comments.length,
    timestamp: lastComment?.timestamp ?? version.timestamp,
    comments,
    last_comment: lastComment,
  };
}

export interface StepChatMessage {
  t_ot_messagesid?: number;
  sender_type?: string;
  sender_id?: number | null;
  sender?: { name?: string; avatar_url?: string };
  message?: string;
  created?: string;
  status_id?: number;
  attached_files?: Array<{ id: number; original_name: string; mime_type: string; file_size: number; url: string }>;
}

/** Convert step chat API messages to a sanitized CommentsVersion for phuture-comments. */
export function buildCommentsVersionFromApiMessages(messages: StepChatMessage[]): CommentsVersion {
  const comments: Comment[] = (messages ?? [])
    .filter((msg) => commentHasDisplayableBody(msg.message, msg.attached_files))
    .map((msg, index) => {
      let senderRole: UserRole = UserRole.Student;
      if (msg.sender_type === 'supervisor') {
        senderRole = UserRole.Supervisor;
      } else if (msg.sender_type === 'system') {
        senderRole = 888 as UserRole;
      }

      return {
        id: msg.t_ot_messagesid || (index + 1),
        sender_id: msg.sender_id || 0,
        sender_role: senderRole,
        sender_name: msg.sender?.name || (msg.sender_type === 'supervisor' ? 'Supervisor' : 'Student'),
        message: msg.message || '',
        timestamp: msg.created || new Date().toISOString(),
        avatar: msg.sender?.avatar_url || './assets/avatar-blank.png',
        isDelivered: true,
        isRead: msg.status_id !== 1,
        attached_files: msg.attached_files,
      } as Comment & { attached_files?: StepChatMessage['attached_files'] };
    });

  const lastComment = comments.length > 0 ? comments[comments.length - 1] : null;

  return sanitizeCommentsVersion({
    version_tag: comments.length,
    timestamp: lastComment?.timestamp || new Date().toISOString(),
    comments,
    last_comment: lastComment,
  });
}

/** Parse legacy supervisor_answer JSON/plain text into a sanitized CommentsVersion. */
export function parseSupervisorAnswerComments(input: unknown): CommentsVersion {
  if (input == null || input === '') {
    return sanitizeCommentsVersion(emptyCommentsVersion());
  }

  try {
    const parsed = typeof input === 'string' ? JSON.parse(input) : input;
    if (
      parsed &&
      typeof parsed.version_tag === 'number' &&
      typeof parsed.timestamp === 'string' &&
      Array.isArray(parsed.comments)
    ) {
      return sanitizeCommentsVersion(parsed as CommentsVersion);
    }
  } catch {
    // fall through to plain-text handling
  }

  if (typeof input === 'string' && input.trim() !== '') {
    const now = new Date().toISOString();
    return sanitizeCommentsVersion({
      version_tag: 1,
      timestamp: now,
      comments: [{
        id: 1,
        sender_id: 0,
        sender_role: UserRole.Supervisor,
        sender_name: 'Supervisor',
        message: input.trim(),
        timestamp: now,
        avatar: './assets/avatar-blank.png',
        isDelivered: false,
        isRead: false,
      }],
      last_comment: null,
    });
  }

  return sanitizeCommentsVersion(emptyCommentsVersion());
}

function emptyCommentsVersion(): CommentsVersion {
  return {
    version_tag: 0,
    timestamp: new Date().toISOString(),
    comments: [],
    last_comment: null,
  };
}
